import type {
  AccessRequest,
  Account,
  AuditEvent,
  BuildArtifact,
  BuildCandidate,
  CannedResponse,
  DocumentDownloadUrlResponse,
  DownloadUrlResponse,
  HubSpotCompany,
  KbArticle,
  LicenseSummary,
  LicenseType,
  Me,
  MentionCandidate,
  NotificationPref,
  PortalDocument,
  PortalLicense,
  PortalUser,
  ReleaseLine,
  ReleaseLineDef,
  SavedView,
  SupportSettings,
  Ticket,
  TicketMetrics,
  UploadRecord,
  UploadUrlResponse,
} from "./types";

/** A file already uploaded (via the log-upload flow) to attach to a ticket/comment. */
export interface TicketAttachmentRef {
  objectPath: string;
  filename: string;
}

/**
 * Thin API client. Every call carries the Identity Platform ID token as a bearer.
 * The backend re-verifies + re-authorizes on every request — the client is untrusted.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export function createApi(
  getToken: () => Promise<string | null>,
  baseUrl = "/api",
  onMfaRequired?: () => void,
) {
  async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await getToken();
    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      let code = "error";
      let message = res.statusText;
      try {
        const body = (await res.json()) as { error?: string; message?: string };
        code = body.error ?? code;
        message = body.message ?? message;
      } catch {
        /* non-JSON error */
      }
      if (code === "mfa_required") onMfaRequired?.();
      throw new ApiError(res.status, code, message);
    }
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  return {
    me: () => call<Me>("/me"),
    // Email-code second factor for every portal user (§F-8). start() emails a fresh code; verify()
    // submits it and, on success, opens the verified window so /me no longer reports mfaRequired.
    mfaStart: () => call<{ sent: boolean }>("/auth/mfa/start", { method: "POST" }),
    mfaVerify: (code: string) =>
      call<{ verified: boolean }>("/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ code }),
      }),
    // Self-service access requests (HubSpot-independent). Public submit + approval.
    submitAccessRequest: (input: {
      email: string;
      name?: string;
      company?: string;
      message?: string;
    }) =>
      call<{ status: string; id?: string }>("/access-requests", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    getAccessRequest: (id: string, token: string) =>
      call<{ request: AccessRequest }>(
        `/access-requests/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`,
      ),
    approveAccessRequest: (id: string, opts?: { token?: string; companyId?: string }) =>
      call<{ status: string; email: string; actionLink: string }>(
        `/access-requests/${encodeURIComponent(id)}/approve`,
        {
          method: "POST",
          body: JSON.stringify({
            ...(opts?.token ? { token: opts.token } : {}),
            ...(opts?.companyId ? { companyId: opts.companyId } : {}),
          }),
        },
      ),
    denyAccessRequest: (id: string, token?: string) =>
      call<{ status: string }>(`/access-requests/${encodeURIComponent(id)}/deny`, {
        method: "POST",
        body: JSON.stringify(token ? { token } : {}),
      }),
    listAccessRequests: () => call<{ requests: AccessRequest[] }>("/admin/access-requests"),
    listBuilds: () => call<{ builds: BuildArtifact[] }>("/builds"),
    downloadUrl: (pipeline: ReleaseLine, objectPath: string) =>
      call<DownloadUrlResponse>("/downloads/url", {
        method: "POST",
        body: JSON.stringify({ pipeline, objectPath }),
      }),
    // Documents library (shared, global to all customers).
    listDocuments: () => call<{ documents: PortalDocument[] }>("/documents"),
    documentDownloadUrl: (id: string) =>
      call<DocumentDownloadUrlResponse>("/documents/url", {
        method: "POST",
        body: JSON.stringify({ id }),
      }),
    listMyLicenses: () => call<{ licenses: LicenseSummary[] }>("/licenses"),
    downloadMyLicense: (id: string) =>
      call<{ pem: string; filename: string }>(`/licenses/${encodeURIComponent(id)}/download`, {
        method: "POST",
      }),
    downloadMyLicenseCertificate: (id: string) =>
      call<{ filename: string; certificate: string }>(
        `/licenses/${encodeURIComponent(id)}/certificate`,
        { method: "POST" },
      ),
    uploadUrl: (filename: string, contentType?: string) =>
      call<UploadUrlResponse>("/uploads/url", {
        method: "POST",
        body: JSON.stringify({ filename, contentType }),
      }),
    completeUpload: (objectPath: string, sizeBytes: number, checksum?: string) =>
      call<{ status: string; ticketId?: string }>("/uploads/complete", {
        method: "POST",
        body: JSON.stringify({ objectPath, sizeBytes, checksum }),
      }),
    // Support tickets (customer) — portal is the front-end for ALL cases. §CUI
    listTickets: () => call<{ tickets: Ticket[] }>("/tickets"),
    getTicket: (id: string) => call<{ ticket: Ticket }>(`/tickets/${encodeURIComponent(id)}`),
    createTicket: (input: {
      subject: string;
      description: string;
      category?: string;
      priority?: string;
      attachments?: TicketAttachmentRef[];
    }) =>
      call<{ ticket: Ticket }>("/tickets", { method: "POST", body: JSON.stringify(input) }),
    addTicketComment: (id: string, input: { body: string; attachments?: TicketAttachmentRef[] }) =>
      call<{ ticket: Ticket }>(`/tickets/${encodeURIComponent(id)}/comments`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    ticketAttachmentUrl: (id: string, objectPath: string) =>
      call<{ url: string; expiresInSeconds: number }>(
        `/tickets/${encodeURIComponent(id)}/attachments/download`,
        { method: "POST", body: JSON.stringify({ objectPath }) },
      ),
    // KB deflection: suggested self-help articles + deflection tracking. §4
    getKbSuggestions: (q: string) =>
      call<{ articles: KbArticle[] }>(`/kb/suggestions?q=${encodeURIComponent(q)}`),
    recordKbDeflection: (articleIds: string[]) =>
      call<{ status: string }>("/kb/deflected", {
        method: "POST",
        body: JSON.stringify({ articleIds }),
      }),
    // AI assistant Q&A over the curated NON-CUI corpus. The asker's own identity is
    // masked by the decoder ring server-side; `answer` is null when AI is disabled or
    // the assistant is unavailable. §AI
    askAi: (question: string) =>
      call<{ answer: string | null }>("/ai/ask", {
        method: "POST",
        body: JSON.stringify({ question }),
      }),
    // Full-text search scoped to the caller's own account. §1.3
    searchTickets: (q: string) =>
      call<{ tickets: Ticket[] }>(`/tickets/search?q=${encodeURIComponent(q)}`),
    // @mention typeahead: same-account teammates + (when a ticket is in scope) the
    // customer users on that ticket's account + PumaMesh admins (never cross-account).
    mentionCandidates: (q: string, ticketId?: string) =>
      call<{ candidates: MentionCandidate[] }>(
        `/tickets/mentionable?q=${encodeURIComponent(q)}${
          ticketId ? `&ticketId=${encodeURIComponent(ticketId)}` : ""
        }`,
      ),
    // Notification preferences (opt-out). §4.3
    getNotificationPrefs: () =>
      call<{ prefs: NotificationPref }>("/tickets/notifications/prefs"),
    setNotificationPrefs: (input: { enabled?: boolean }) =>
      call<{ prefs: NotificationPref }>("/tickets/notifications/prefs", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    // Login-less CSAT submission (token from the survey link). §1.5
    submitCsat: (id: string, token: string, score: number, comment?: string) =>
      call<{ status: string }>(`/tickets/${encodeURIComponent(id)}/csat`, {
        method: "POST",
        body: JSON.stringify({ token, score, comment }),
      }),
    // Admin
    createAccount: (
      name: string,
      releaseLines?: ReleaseLine[],
      opts?: { hubspotCompanyId?: string; createInHubspot?: boolean },
    ) =>
      call<{ account: Account }>("/admin/accounts", {
        method: "POST",
        body: JSON.stringify({ name, releaseLines, ...opts }),
      }),
    listAccounts: () => call<{ accounts: Account[] }>("/admin/accounts"),
    listHubspotCompanies: (q?: string) =>
      call<{ enabled: boolean; companies: HubSpotCompany[] }>(
        `/admin/hubspot/companies${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      ),
    linkAccountHubspot: (
      companyId: string,
      hubspotCompanyId: string | null,
      opts?: { createInHubspot?: boolean },
    ) =>
      call<{ account: Account }>(
        `/admin/accounts/${encodeURIComponent(companyId)}/hubspot-link`,
        { method: "POST", body: JSON.stringify({ hubspotCompanyId, ...opts }) },
      ),
    listAccountUsers: (companyId: string) =>
      call<{ users: PortalUser[] }>(
        `/admin/accounts/${encodeURIComponent(companyId)}/users`,
      ),
    inviteUser: (input: {
      email: string;
      companyId: string;
      role: string;
      firstName?: string;
      lastName?: string;
    }) =>
      call<{ user: { contactId: string }; actionLink: string }>("/admin/users/invite", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    setEntitlements: (companyId: string, releaseLines: ReleaseLine[]) =>
      call<{ status: string }>(`/admin/accounts/${companyId}/entitlements`, {
        method: "PATCH",
        body: JSON.stringify({ releaseLines }),
      }),
    deleteAccount: (companyId: string) =>
      call<{ status: string }>(`/admin/accounts/${encodeURIComponent(companyId)}`, {
        method: "DELETE",
      }),
    updateAccount: (
      companyId: string,
      patch: { name?: string; status?: "active" | "suspended"; releaseLines?: ReleaseLine[] },
    ) =>
      call<{ account: Account }>(`/admin/accounts/${encodeURIComponent(companyId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    accountBucketStatus: () =>
      call<{ status: Record<string, boolean> }>("/admin/accounts/bucket-status"),
    provisionBucket: (companyId: string) =>
      call<{ bucket: string; created: boolean }>(
        `/admin/accounts/${encodeURIComponent(companyId)}/bucket`,
        { method: "POST" },
      ),
    deleteBucket: (companyId: string) =>
      call<{ status: string }>(`/admin/accounts/${encodeURIComponent(companyId)}/bucket`, {
        method: "DELETE",
      }),
    setUserRole: (contactId: string, role: string, companyId?: string) =>
      call<{ status: string }>("/admin/users/role", {
        method: "POST",
        body: JSON.stringify({ contactId, role, companyId }),
      }),
    /** Email an existing user a fresh set-password link. */
    sendPasswordReset: (contactId: string, companyId: string) =>
      call<{ status: string; email: string }>("/admin/users/password-reset", {
        method: "POST",
        body: JSON.stringify({ contactId, companyId }),
      }),
    /** Re-home an existing portal-local user into another account. */
    moveUser: (contactId: string, companyId: string) =>
      call<{ user: PortalUser; account: Account }>("/admin/users/move", {
        method: "POST",
        body: JSON.stringify({ contactId, companyId }),
      }),
    inviteLocalUser: (input: {
      email: string;
      companyId: string;
      role: string;
      name?: string;
    }) =>
      call<{ user: PortalUser; actionLink: string }>("/admin/users/invite-local", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    deactivateUser: (contactId: string, uid?: string, companyId?: string) =>
      call<{ status: string }>("/admin/users/deactivate", {
        method: "POST",
        body: JSON.stringify({ contactId, uid, companyId }),
      }),
    reactivateUser: (contactId: string, uid?: string, companyId?: string) =>
      call<{ status: string }>("/admin/users/reactivate", {
        method: "POST",
        body: JSON.stringify({ contactId, uid, companyId }),
      }),
    deleteUser: (contactId: string, uid?: string, companyId?: string) =>
      call<{ status: string }>("/admin/users/delete", {
        method: "POST",
        body: JSON.stringify({ contactId, uid, companyId }),
      }),
    activity: (portalAccountId?: string) =>
      call<{ events: AuditEvent[] }>(
        `/admin/activity${portalAccountId ? `?portalAccountId=${portalAccountId}` : ""}`,
      ),
    // Admin — build catalog / GA promotion
    listReleaseLines: () => call<{ lines: ReleaseLineDef[] }>("/admin/release-lines"),
    listBuildCandidates: (line?: ReleaseLine) =>
      call<{ lines: Record<string, BuildCandidate[]> }>(
        `/admin/builds${line ? `?line=${line}` : ""}`,
      ),
    // Admin — build upload + registration (define a build)
    mintBuildUploadUrl: (line: string, filename: string, contentType?: string) =>
      call<UploadUrlResponse>("/admin/builds/upload-url", {
        method: "POST",
        body: JSON.stringify({ line, filename, contentType }),
      }),
    registerBuild: (input: {
      line: string;
      objectPath: string;
      version?: string;
      buildNumber?: string;
      os?: string;
      arch?: string;
      sha256?: string;
      blake3?: string;
      signatureTool?: string;
      signatureKeyId?: string;
      signaturePath?: string;
      releaseNotesUrl?: string;
    }) =>
      call<{ artifact: BuildArtifact }>("/admin/builds/register", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    promoteBuild: (line: ReleaseLine, objectPath: string) =>
      call<{ promoted: BuildArtifact; pruned: string[] }>("/admin/builds/promote", {
        method: "POST",
        body: JSON.stringify({ line, objectPath }),
      }),
    promotePair: (portalPath: string, policyPath: string) =>
      call<{ states: ["committed", "committed"]; pruned: string[]; audits: string[]; live: boolean }>("/admin/builds/promote-pair", {
        method: "POST", body: JSON.stringify({ portalPath, policyPath }),
      }),
    demoteBuild: (line: ReleaseLine, objectPath: string) =>
      call<{ status: string }>("/admin/builds/demote", {
        method: "POST",
        body: JSON.stringify({ line, objectPath }),
      }),
    // Admin — documents library (publish PDFs with titles)
    listAdminDocuments: () => call<{ documents: PortalDocument[] }>("/admin/documents"),
    mintDocumentUploadUrl: (filename: string, contentType?: string) =>
      call<UploadUrlResponse>("/admin/documents/upload-url", {
        method: "POST",
        body: JSON.stringify({ filename, contentType }),
      }),
    publishDocument: (input: {
      objectPath: string;
      title: string;
      description?: string;
      category?: string;
      contentType?: string;
    }) =>
      call<{ document: PortalDocument }>("/admin/documents", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    deleteDocument: (id: string) =>
      call<{ status: string }>(`/admin/documents/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    // Admin — licenses (issue/list/revoke/download signed customer license files)
    listLicenses: () =>
      call<{ enabled: boolean; licenses: PortalLicense[] }>("/admin/licenses"),
    createLicense: (input: {
      type: LicenseType;
      count: number;
      termYears: number;
      companyId?: string;
      customerName?: string;
      customerId?: string;
      notes?: string;
    }) =>
      call<{ license: PortalLicense }>("/admin/licenses", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    revokeLicense: (id: string) =>
      call<{ license: PortalLicense }>(`/admin/licenses/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
      }),
    downloadLicense: (id: string) =>
      call<{ pem: string; filename: string }>(
        `/admin/licenses/${encodeURIComponent(id)}/download`,
        { method: "POST" },
      ),
    downloadLicenseCertificate: (id: string) =>
      call<{ filename: string; certificate: string }>(
        `/admin/licenses/${encodeURIComponent(id)}/certificate`,
        { method: "POST" },
      ),
    emailLicense: (id: string, input: { to: string[]; cc?: string[]; includeCertificate?: boolean }) =>
      call<{ to: string[]; cc: string[]; sentAt: string }>(
        `/admin/licenses/${encodeURIComponent(id)}/email`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    // Admin — support logs
    listUploads: (portalAccountId?: string) =>
      call<{ uploads: UploadRecord[] }>(
        `/admin/logs${portalAccountId ? `?portalAccountId=${portalAccountId}` : ""}`,
      ),
    logDownloadUrl: (bucket: string, objectPath: string) =>
      call<{ url: string; expiresInSeconds: number }>("/admin/logs/download", {
        method: "POST",
        body: JSON.stringify({ bucket, objectPath }),
      }),
    // Admin — support tickets (all accounts, full CUI + internal notes)
    adminListTickets: () => call<{ tickets: Ticket[] }>("/admin/tickets"),
    adminGetTicket: (id: string) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}`),
    adminUpdateTicket: (
      id: string,
      patch: {
        status?: string;
        priority?: string;
        assigneeEmail?: string | null;
        expectedUpdatedAt?: string;
      },
    ) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    adminAddTicketComment: (
      id: string,
      input: { body: string; internal?: boolean; attachments?: TicketAttachmentRef[] },
    ) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}/comments`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    adminTicketAttachmentUrl: (id: string, objectPath: string) =>
      call<{ url: string; expiresInSeconds: number }>(
        `/admin/tickets/${encodeURIComponent(id)}/attachments/download`,
        { method: "POST", body: JSON.stringify({ objectPath }) },
      ),
    // Admin — retry a stuck ("pending") malware scan on this ticket's attachments after a
    // transient scanner outage, without waiting for the scheduled sweep. §4 F-5
    adminRescanTicketAttachments: (id: string) =>
      call<{ scanned: number; cleared: number; infected: number; stillPending: number }>(
        `/admin/tickets/${encodeURIComponent(id)}/rescan-attachments`,
        { method: "POST" },
      ),
    // Admin — full-text search across every ticket (subject/description/comments). §1.3
    adminSearchTickets: (q: string) =>
      call<{ tickets: Ticket[] }>(`/admin/tickets/search?q=${encodeURIComponent(q)}`),
    // Admin — reporting aggregates (CUI-free). §3
    adminTicketMetrics: (from?: string, to?: string) => {
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const suffix = qs.toString();
      return call<{ metrics: TicketMetrics }>(
        `/admin/tickets/metrics${suffix ? `?${suffix}` : ""}`,
      );
    },
    // Admin — bulk status/priority/assignee across many tickets. §2
    adminBulkTickets: (
      ids: string[],
      patch: { status?: string; priority?: string; assigneeEmail?: string | null },
    ) =>
      call<{ updated: number }>("/admin/tickets/bulk", {
        method: "POST",
        body: JSON.stringify({ ids, ...patch }),
      }),
    adminSetTicketTags: (id: string, tags: string[]) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}/tags`, {
        method: "PATCH",
        body: JSON.stringify({ tags }),
      }),
    adminSetTicketWatchers: (id: string, watchers: string[]) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}/watchers`, {
        method: "PATCH",
        body: JSON.stringify({ watchers }),
      }),
    adminLinkTicketBuild: (id: string, objectPath: string | null) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(id)}/build-link`, {
        method: "PATCH",
        body: JSON.stringify({ objectPath }),
      }),
    adminMergeTicket: (sourceId: string, targetId: string) =>
      call<{ ticket: Ticket }>(`/admin/tickets/${encodeURIComponent(sourceId)}/merge`, {
        method: "POST",
        body: JSON.stringify({ targetId }),
      }),
    // Admin — canned responses / macros CRUD. §2
    listCannedResponses: () => call<{ canned: CannedResponse[] }>("/admin/canned-responses"),
    upsertCannedResponse: (input: { id?: string; title: string; body: string }) =>
      call<{ canned: CannedResponse }>("/admin/canned-responses", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    deleteCannedResponse: (id: string) =>
      call<{ status: string }>(`/admin/canned-responses/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    // Admin — saved views CRUD. §2
    listSavedViews: () => call<{ views: SavedView[] }>("/admin/saved-views"),
    saveSavedView: (input: { id?: string; name: string; filter: Record<string, unknown> }) =>
      call<{ view: SavedView }>("/admin/saved-views", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    deleteSavedView: (id: string) =>
      call<{ status: string }>(`/admin/saved-views/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    // Admin — support settings (auto-assignment queues tunable). §2
    getSupportSettings: () =>
      call<{ settings: SupportSettings }>("/admin/support/settings"),
    setSupportSettings: (patch: { queues?: Record<string, string[]> }) =>
      call<{ settings: SupportSettings }>("/admin/support/settings", {
        method: "PUT",
        body: JSON.stringify(patch),
      }),
    // Admin — escalation / auto-close sweep (also driven by Cloud Scheduler). §2
    runSlaSweep: () =>
      call<{ escalated: number; autoClosed: number }>("/admin/sla/sweep", { method: "POST" }),
  };
}

export type Api = ReturnType<typeof createApi>;

/** Upload a file to a signed resumable-upload URL and report progress. §4.4 */
export async function uploadToSignedUrl(
  signedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  // Initiate the resumable session.
  const initRes = await fetch(signedUrl, {
    method: "POST",
    headers: {
      "content-type": file.type || "application/octet-stream",
      "x-goog-resumable": "start",
    },
  });
  if (!initRes.ok) {
    const detail = (await initRes.text()).trim().replace(/\s+/g, " ").slice(0, 240);
    throw new Error(
      `Failed to start upload session (${initRes.status})${detail ? `: ${detail}` : ""}`,
    );
  }
  const sessionUrl = initRes.headers.get("location");
  if (!sessionUrl) throw new Error("No resumable session URL returned by GCS.");

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", sessionUrl, true);
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      const detail = xhr.responseText.trim().replace(/\s+/g, " ").slice(0, 240);
      reject(new Error(`Upload failed (${xhr.status})${detail ? `: ${detail}` : ""}`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
}
